import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { Tenant, Membership } from '../lib/types'
import { useAuth } from './AuthContext'

interface TenantContextType {
  memberships: Membership[]
  tenants: Tenant[]
  currentTenant: Tenant | null
  loading: boolean
  setCurrentTenant: (tenant: Tenant | null) => void
  refreshMemberships: () => Promise<void>
  isPlatformAdmin: boolean
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth()
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)

  useEffect(() => {
    if (user && profile) {
      fetchMemberships()
      checkPlatformAdmin()
    } else {
      setMemberships([])
      setTenants([])
      setCurrentTenant(null)
      setIsPlatformAdmin(false)
      setLoading(false)
    }
  }, [user, profile])

  const fetchMemberships = async () => {
    if (!user) return

    try {
      setLoading(true)

      // Membershipsを取得
      const { data: membershipsData, error: membershipsError } = await supabase
        .from('memberships')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')

      if (membershipsError) throw membershipsError

      setMemberships(membershipsData || [])

      // Tenantsを取得
      if (membershipsData && membershipsData.length > 0) {
        const tenantIds = membershipsData.map((m) => m.tenant_id)
        const { data: tenantsData, error: tenantsError } = await supabase
          .from('tenants')
          .select('*')
          .in('tenant_id', tenantIds)
          .eq('status', 'active')

        if (tenantsError) throw tenantsError

        setTenants(tenantsData || [])

        // 最初のテナントをcurrentTenantに設定（未設定の場合）
        if (!currentTenant && tenantsData && tenantsData.length > 0) {
          setCurrentTenant(tenantsData[0])
        }
      }
    } catch (error) {
      console.error('Error fetching memberships:', error)
    } finally {
      setLoading(false)
    }
  }

  const checkPlatformAdmin = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('platform_admins')
        .select('admin_id')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') throw error
      setIsPlatformAdmin(!!data)
    } catch (error) {
      console.error('Error checking platform admin:', error)
      setIsPlatformAdmin(false)
    }
  }

  const refreshMemberships = async () => {
    await fetchMemberships()
  }

  const value = {
    memberships,
    tenants,
    currentTenant,
    loading,
    setCurrentTenant,
    refreshMemberships,
    isPlatformAdmin,
  }

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
